import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export const getAssetUrl = (path) => {
  if (!path) return '';
  // If it's already an external HTTP link, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) return path;

  // Merge backend URL with local path safely
  const baseUrl = API_BASE_URL.replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${baseUrl}${cleanPath}`;
};

// Secure keycloak getter - no global exposure
let getKeycloakInstance = null;

export const setKeycloakGetter = (getter) => {
  getKeycloakInstance = getter;
};

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    // Skip auth for requests with skipAuth flag
    if (config.skipAuth) {
      delete config.skipAuth; // Remove flag before sending request
      return config;
    }

    // Get token securely from Keycloak getter
    const keycloakInstance = getKeycloakInstance ? getKeycloakInstance() : null;

    if (keycloakInstance && keycloakInstance.authenticated) {
      // Try to refresh token if it expires in less than 30 seconds
      // This also covers the case when token is already expired
      try {
        await keycloakInstance.updateToken(30);
      } catch (error) {
        console.error('Portal: Failed to refresh token:', error);
        keycloakInstance.login();
        return Promise.reject(new Error('Failed to refresh token'));
      }

      const token = keycloakInstance.token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're currently redirecting to prevent multiple redirects
let isRedirectingToLogin = false;

// Export reset function for use in AuthContext
export const resetRedirectFlag = () => {
  isRedirectingToLogin = false;
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // 401 - Unauthorized (token expired, revoked, or invalid)
    if (status === 401) {
      const errorMessage = error.response?.data?.message || '';
      const isSessionExpired = errorMessage.includes('session expired') ||
        errorMessage.includes('session expired or revoked') ||
        errorMessage.includes('no longer active');

      console.error('[API] 401 Unauthorized:', errorMessage);

      // Redirect to login (with protection against multiple redirects)
      if (!isRedirectingToLogin) {
        isRedirectingToLogin = true;

        // Get keycloak instance securely and redirect
        const keycloakInstance = getKeycloakInstance ? getKeycloakInstance() : null;
        if (keycloakInstance) {
          // Don't show alert for session expiration (will redirect anyway)
          if (!isSessionExpired) {
            console.warn('[API] Token validation failed:', errorMessage);
          }

          // Small delay to allow other pending requests to see the flag
          setTimeout(() => {
            keycloakInstance.login();
          }, 100);
        } else {
          // Fallback: reload page (will trigger check-sso)
          setTimeout(() => {
            window.location.href = '/';
          }, 100);
        }
      }
    }

    // 403 - Forbidden (insufficient permissions)
    if (status === 403) {
      console.error('[API] 403 Forbidden - Insufficient permissions');
      // You might want to show a "Access Denied" page
    }

    // 5xx - Server errors
    if (status >= 500) {
      console.error('[API] Server error:', status);
      // You might want to show a "Service Unavailable" message
    }

    return Promise.reject(error);
  }
);

// Applications API
export const applicationsApi = {
  getAll: (params) => api.get('/applications', { params }),
  getById: (id) => api.get(`/applications/${id}`),
  create: (data) => api.post('/applications', data),
  update: (id, data) => api.put(`/applications/${id}`, data),
  delete: (id) => api.delete(`/applications/${id}`),
  uploadIcon: (formData) => api.post('/applications/upload-icon', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteIcon: (url) => api.post('/applications/delete-icon', { iconUrl: url }),
};

// Users API
export const usersApi = {
  getMe: () => api.get('/users/me'),
  getAll: () => api.get('/users'),
  getUserRoles: (id) => api.get(`/users/${id}/roles`),

  // Client roles management
  getClients: () => api.get('/users/clients'),
  getClientRoles: (clientId) => api.get(`/users/clients/${clientId}/roles`),
  getUserClientRoles: (userId, clientId) => api.get(`/users/${userId}/clients/${clientId}/roles`),
  assignClientRole: (userId, clientId, roleId, roleName) =>
    api.post(`/users/${userId}/clients/${clientId}/roles`, { roleId, roleName }),
  removeClientRole: (userId, clientId, roleId, roleName) =>
    api.delete(`/users/${userId}/clients/${clientId}/roles/${roleId}`, { params: { roleName } }),
};

// Audit API
export const auditApi = {
  logAccess: (applicationId) => api.post('/audit/access', { applicationId }),
  getLogs: (params) => api.get('/audit/logs', { params }),
  getStats: (params) => api.get('/audit/stats', { params }),
};

// Auth API
export const authApi = {
  verify: () => api.get('/auth/verify'),
  logout: () => api.post('/auth/logout'),
};

// Backgrounds API
export const backgroundsApi = {
  // Public/user endpoints
  getActive: () => api.get('/backgrounds/active'),
  getUserBackground: () => api.get('/backgrounds/user'),
  uploadUserBackground: (file) => {
    const formData = new FormData();
    formData.append('background', file);
    return api.post('/backgrounds/user', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteUserBackground: () => api.delete('/backgrounds/user'),

  // Admin endpoints
  getAllGlobal: () => api.get('/backgrounds/admin/global'),
  uploadGlobalBackground: (file, validFrom, validUntil) => {
    const formData = new FormData();
    formData.append('background', file);
    formData.append('validFrom', validFrom);
    formData.append('validUntil', validUntil);
    return api.post('/backgrounds/admin/global', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateGlobalBackground: (id, data) => api.patch(`/backgrounds/admin/global/${id}`, data),
  deleteGlobalBackground: (id) => api.delete(`/backgrounds/admin/global/${id}`),
};

export default api;
